import { h, type VNode } from 'vue';

/**
 * The four player type glyphs, from the official GrayV icon set (300×300
 * viewBox). Type ids follow tabasco's PlayerType: 1 PHYSICAL, 2 VIRTUAL,
 * 3 MOBILE (the icon set calls it "in-store"), 4 WSL (web).
 *
 * modelIcons draws them as a player's icon in the player colour, and
 * PlayerTypeIcon adds a tooltip around the same icon.
 */
export type PlayerTypeKey = 'physical' | 'virtual' | 'mobile' | 'web';

export const PLAYER_TYPES: Readonly<Record<number, { key: PlayerTypeKey; label: string }>> = Object.freeze({
    1: { key: 'physical', label: 'Physical player' },
    2: { key: 'virtual', label: 'Virtual player' },
    3: { key: 'mobile', label: 'Mobile player' },
    4: { key: 'web', label: 'Web player' },
});

/**
 * The web glyph ships two-tone (dark base + white globe knockout). It is
 * flattened into a single fill-rule="evenodd" path — base subpath, then the
 * globe subpaths — so the globe is a true cutout showing whatever background
 * the icon sits on (base = 1 crossing → filled, globe circle = 2 → hole,
 * grid segments = 3 → filled).
 */
const WEB_ICON_PATH =
    'M235,50H65c-16.6,0-30,13.4-30,30v140c0,16.6,13.4,30,30,30l0,0h170c16.6,0,30-13.4,30-30l0,0V80' +
    'C265,63.4,251.6,50,235,50z' +
    'M150,74.5c41.7,0,75.5,33.8,75.5,75.5s-33.8,75.5-75.5,75.5S74.5,191.7,74.5,150S108.3,74.5,150,74.5' +
    'M166.1,175.2h-32.2c3.4,15.5,9.1,25.9,16.1,36.1C157.5,200.3,162.8,189.8,166.1,175.2' +
    'M121.1,175.2H92.3c7.8,17.8,23.3,31.3,42.5,35.9C128.2,200.2,123.6,188.1,121.1,175.2' +
    'M207.7,175.2H179c-2.5,12.5-6.9,24.4-13.7,35.8C184.2,206.2,199.9,192.8,207.7,175.2' +
    'M119.6,137.4H88.4c-1.7,8.3-1.7,16.9,0,25.2h30.9C118.5,154.2,118.6,145.8,119.6,137.4' +
    'M167.8,137.4h-35.5c-1,8.3-1.2,16.8-0.3,25.2h36.2C168.9,154.2,168.8,145.8,167.8,137.4' +
    'M211.6,137.4h-31.2c0.9,8.4,1,16.8,0.3,25.2h30.9C213.3,154.5,213.3,145.9,211.6,137.4' +
    'M135.2,88.8c-19.4,4.6-35,18.1-42.8,36h29.3C124.3,112,129,99.8,135.2,88.8' +
    'M150,88.5c-6.8,11.1-12,21.7-15.5,36.3h31C162.1,110.7,157.1,100,150,88.5' +
    'M164.9,88.9c6.4,11.4,10.9,23.6,13.5,35.9h29.3C199.9,107.1,184,93.7,164.9,88.9';

function glyphShapes(key: PlayerTypeKey): VNode[] {
    switch (key) {
        case 'physical':
            return [
                h('path', { d: 'M150,180.44A30.44,30.44,0,1,1,180.44,150,30.44,30.44,0,0,1,150,180.44Z' }),
                h('path', {
                    d: 'M235,50H65A30,30,0,0,0,35,80V220a30,30,0,0,0,30,30H235a30,30,0,0,0,30-30V80A30,30,0,0,0,235,50ZM150,236.25A86.25,86.25,0,1,1,236.25,150,86.26,86.26,0,0,1,150,236.25Z',
                }),
            ];
        case 'virtual':
            return [
                h('circle', { cx: 65, cy: 80, r: 30 }),
                h('path', { d: 'M155,110a30,30,0,0,1,0-60h80a30,30,0,0,1,0,60Z' }),
                h('path', { d: 'M155,250a30,30,0,0,1,0-60h80a30,30,0,0,1,0,60Z' }),
                h('circle', { cx: 65, cy: 220, r: 30 }),
            ];
        case 'mobile':
            return [
                h('rect', { x: 35, y: 50, width: 40, height: 200 }),
                h('rect', { x: 225, y: 50, width: 40, height: 200 }),
                h('rect', { x: 100, y: 50, width: 100, height: 200, rx: 30 }),
            ];
        case 'web':
            return [h('path', { 'fill-rule': 'evenodd', d: WEB_ICON_PATH })];
    }
}

/** The glyph as an `<svg>` filled with the current text colour. */
export function drawPlayerTypeGlyph(key: PlayerTypeKey): VNode {
    return h(
        'svg',
        { viewBox: '0 0 300 300', xmlns: 'http://www.w3.org/2000/svg', fill: 'currentColor', 'data-player-type': key },
        glyphShapes(key)
    );
}
